import { mapHttpError } from '@/lib/mapApiError';
import { getBackErrorCodes } from '@/lib/evergreenInteractionSchedule';

export type SourceSessionRefusal =
  | 'SOURCE_SESSION_DURATION_EXCEEDED'
  | 'SOURCE_SESSION_NOT_STARTED'
  | 'SOURCE_VIDEO_DURATION_UNAVAILABLE';

/** Refus définitifs liés au Live choisi : réessayer ne sert à rien, il faut en choisir un autre. */
export const SOURCE_SESSION_REFUSALS: Readonly<Record<string, SourceSessionRefusal>> = {
  'HTTP.ERROR.403.EVERGREEN.SOURCE_SESSION.DURATION_EXCEEDED': 'SOURCE_SESSION_DURATION_EXCEEDED',
  'HTTP.ERROR.403.EVERGREEN.SOURCE_SESSION_NOT_STARTED': 'SOURCE_SESSION_NOT_STARTED',
  'HTTP.ERROR.409.EVERGREEN.SOURCE_VIDEO.DURATION.UNAVAILABLE': 'SOURCE_VIDEO_DURATION_UNAVAILABLE',
};

const SOURCE_TIMELINE_PREPARING_CODES = [
  'HTTP.ERROR.409.EVERGREEN.SOURCE_TIMELINE.PREPARING',
  'HTTP.ERROR.409.EVERGREEN.INTERACTION_SOURCE.TIMELINE.PREPARING',
];
const SOURCE_TIMELINE_PREPARING_KEY = 'errors.evergreenSourceTimelinePreparing';

/**
 * Un live antérieur au manifeste vidéo est d'abord préparé par le back, qui répond 409 sans attendre
 * la fin du job (quelques secondes) : la conversion se relance jusqu'à ce que le manifeste existe.
 */
export const EVERGREEN_PREPARING_RETRY_INTERVAL_MS = 3_000;
export const EVERGREEN_PREPARING_MAX_WAIT_MS = 90_000;

function readErrorMessage(error: unknown): unknown {
  return (error as { message?: unknown } | null)?.message;
}

/** Reconnaît la 409 « en préparation », brute (réponse HTTP) ou déjà traduite en clé i18n. */
export function isEvergreenSourceTimelinePreparing(error: unknown): boolean {
  const message = readErrorMessage(error);
  return (
    message === SOURCE_TIMELINE_PREPARING_KEY ||
    (typeof message === 'string' && SOURCE_TIMELINE_PREPARING_CODES.includes(message))
  );
}

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** Relance l'appel tant que le back prépare la timeline du Live source, dans la limite d'attente. */
export async function retryWhileSourceTimelinePreparing<T>(
  call: () => Promise<T>,
  {
    sleep = defaultSleep,
    now = Date.now,
    signal,
  }: { sleep?: (ms: number) => Promise<void>; now?: () => number; signal?: AbortSignal } = {},
): Promise<T> {
  const deadline = now() + EVERGREEN_PREPARING_MAX_WAIT_MS;
  while (true) {
    try {
      return await call();
    } catch (error) {
      if (!isEvergreenSourceTimelinePreparing(error) || now() >= deadline || signal?.aborted) {
        throw error;
      }
      await sleep(EVERGREEN_PREPARING_RETRY_INTERVAL_MS);
    }
  }
}

const YOUTUBE_START_TIME_UNAVAILABLE_KEY = 'errors.evergreenYoutubeStartTimeUnavailable';

/** Message affiché quand la conversion d'un live en evergreen est refusée. */
export function resolveEvergreenConvertErrorKey(error: unknown): string {
  if (isEvergreenSourceTimelinePreparing(error)) return SOURCE_TIMELINE_PREPARING_KEY;
  for (const code of getBackErrorCodes(error)) {
    const refusal = SOURCE_SESSION_REFUSALS[code];
    if (refusal) return `createWebinar:sessionForm.sources.saveErrors.${refusal}`;
  }
  const message = readErrorMessage(error);
  return typeof message === 'string' && mapHttpError(message) === YOUTUBE_START_TIME_UNAVAILABLE_KEY
    ? YOUTUBE_START_TIME_UNAVAILABLE_KEY
    : 'errors.generic';
}
